/** Мелкие детали интерфейса, повторяющиеся на разных экранах.
 *
 * Собраны в одном файле не ради экономии, а чтобы у чипа баланса и шкалы
 * сложности была одна реализация: разойдясь, они начнут показывать одно
 * и то же по-разному на соседних экранах.
 */

import type { ReactNode } from 'react'

import type { Difficulty } from '../api/types'
import { t } from '../i18n'

/** Порядок уровней. Он же задаёт номер цвета в шкале сложности. */
export const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard', 'expert', 'master']

export function levelIndex(difficulty: Difficulty): number {
  return DIFFICULTIES.indexOf(difficulty) + 1
}

/** Кристалл. Символ один на всё приложение — иначе валюта выглядит разной. */
export function Crystal({ children }: { children?: ReactNode }) {
  return (
    <>
      <span className="crystal">◆</span>
      {children !== undefined && <span className="num">{children}</span>}
    </>
  )
}

export function TopBar({
  title,
  onBack,
  right,
}: {
  title: string
  onBack?: () => void
  right?: ReactNode
}) {
  return (
    <header className="topbar">
      {onBack ? (
        <button type="button" className="topbar__action" onClick={onBack} aria-label={t('common.back')}>
          ‹
        </button>
      ) : (
        <span className="topbar__spacer" />
      )}
      <span className="topbar__title">{title}</span>
      {right ?? <span className="topbar__spacer" />}
    </header>
  )
}

export function BalanceChip({ balance }: { balance: number }) {
  return (
    <span className="chip chip--balance">
      <Crystal>{balance}</Crystal>
    </span>
  )
}

/**
 * Шкала сложности: число полос, их высота и цвет.
 *
 * Три признака сразу, и любой из них читается без подписи — подпись рядом
 * подтверждает, но не требуется.
 */
export function LevelBars({ difficulty }: { difficulty: Difficulty }) {
  const level = levelIndex(difficulty)
  return (
    <span className="level-bars" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((index) => (
        <span
          key={index}
          className={`level-bars__bar ${index <= level ? 'level-bars__bar--on' : ''}`.trim()}
          style={{
            height: `${level * 20}%`,
            ['--level-color' as string]: `var(--level-${level})`,
          }}
        />
      ))}
    </span>
  )
}

/** Цветная метка уровня в чипе партии — та же шкала, но одной полосой. */
export function LevelMark({ difficulty }: { difficulty: Difficulty }) {
  return (
    <span
      className="level-mark"
      style={{ background: `var(--level-${levelIndex(difficulty)})` }}
    />
  )
}

export function ProgressBar({
  value,
  total,
  variant,
}: {
  value: number
  total: number
  variant?: 'accent' | 'success'
}) {
  const share = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0
  return (
    <div
      className={`progress ${variant === 'accent' ? 'progress--on-accent' : ''} ${
        variant === 'success' ? 'progress--success' : ''
      }`.trim()}
    >
      <div className="progress__fill" style={{ width: `${share}%` }} />
    </div>
  )
}

export function Switch({
  on,
  onToggle,
  label,
}: {
  on: boolean
  onToggle: () => void
  label: string
}) {
  return (
    <button
      type="button"
      className={`switch ${on ? 'switch--on' : ''}`.trim()}
      onClick={onToggle}
      role="switch"
      aria-checked={on}
      aria-label={label}
    >
      <span className="switch__knob" />
    </button>
  )
}

/** Место в таблице: первые три — медалями, остальные обычным цветом. */
export function Rank({ rank }: { rank: number }) {
  return <span className={`rank num ${rank <= 3 ? `rank--${rank}` : ''}`.trim()}>{rank}</span>
}
